import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, LogOut, Save, Plus, ChevronDown, Link2, Activity, Copy, Check, Lock, Unlock } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";

const INPUT =
  "w-full border border-black/15 bg-white px-4 py-3 text-sm rounded-sm focus:outline-none focus:ring-1 focus:ring-volt focus:border-volt transition-colors duration-200";

const STATUS_COLORS = {
  available: "bg-black/10 text-black/60",
  assigned: "bg-amber-100 text-amber-700",
  active: "bg-green-100 text-green-700",
  suspended: "bg-red-100 text-red-600",
  damaged: "bg-red-100 text-red-600",
  replaced: "bg-black/10 text-black/50",
  archived: "bg-black/10 text-black/40",
};

export default function Admin() {
  const [token, setToken] = useState(localStorage.getItem("revora_admin") || "");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [orders, setOrders] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [packages, setPackages] = useState([]);
  const [assets, setAssets] = useState([]);
  const [assetStatuses, setAssetStatuses] = useState([]);
  const [baseUrl, setBaseUrl] = useState("");
  const [analytics, setAnalytics] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [expandedOrder, setExpandedOrder] = useState(null);
  const [copiedUrl, setCopiedUrl] = useState(null);
  const [edit, setEdit] = useState({});
  const [loginError, setLoginError] = useState("");

  const authed = (t = token) => ({ headers: { Authorization: `Bearer ${t}` } });

  const load = async (t = token) => {
    try {
      const [{ data: o }, { data: p }, { data: a }, { data: an }] = await Promise.all([
        api.get("/admin/orders", authed(t)),
        api.get("/packages"),
        api.get("/admin/assets", authed(t)),
        api.get("/admin/analytics/summary", authed(t)),
      ]);
      setOrders(o.orders);
      setStatuses(o.statuses);
      setPackages(p.packages);
      setAssets(a.assets);
      setAssetStatuses(a.statuses);
      setBaseUrl(a.base_url);
      setAnalytics(an);
    } catch {
      setToken("");
      localStorage.removeItem("revora_admin");
    }
  };

  useEffect(() => {
    if (token) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const login = async (e) => {
    e.preventDefault();
    setLoginError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      localStorage.setItem("revora_admin", data.token);
      setToken(data.token);
    } catch (err) {
      setLoginError(formatApiError(err));
    }
  };

  const setStatus = async (orderNumber, status) => {
    try {
      await api.patch(`/admin/orders/${orderNumber}/status`, { status }, authed());
      setOrders((os) => os.map((o) => (o.order_number === orderNumber ? { ...o, status } : o)));
      toast.success(`Order ${orderNumber} → ${status}`);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const assignAssets = async (orderNumber) => {
    try {
      const { data } = await api.post(`/admin/orders/${orderNumber}/assign-assets`, {}, authed());
      toast.success(`Assigned ${data.assigned_assets.length} assets: ${data.assigned_assets.slice(0, 3).join(", ")}${data.assigned_assets.length > 3 ? "…" : ""}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const savePrices = async () => {
    try {
      await api.put("/admin/packages", { packages }, authed());
      toast.success("Prices updated");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const generate = async (test) => {
    try {
      const { data } = await api.post("/admin/assets/generate", { count: test ? 3 : 10, test }, authed());
      toast.success(`Generated ${data.count} ${test ? "test " : ""}assets`);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const openEdit = (a) => {
    setExpanded(expanded === a.asset_id ? null : a.asset_id);
    setEdit({
      destination_url: a.destination_url || "",
      business_name: a.business_name || "",
      location_name: a.location_name || "",
      staff_name: a.staff_name || "",
      status: a.status,
      reason: "",
    });
  };

  const saveAsset = async (assetId, asset) => {
    if (asset?.programming_locked && !edit.reason?.trim()) {
      toast.error("This item is programmed — enter a reason (audit-logged) before saving changes.");
      return;
    }
    try {
      const { data } = await api.patch(`/admin/assets/${assetId}`, edit, authed());
      toast.success(data.changed === false ? `${assetId} unchanged` : `${assetId} updated`);
      setExpanded(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const programAsset = async (assetId) => {
    try {
      await api.post(`/admin/assets/${assetId}/program`, {}, authed());
      toast.success(`${assetId} marked as programmed — asset ID, redirect URL and order assignment are now locked`);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const unprogramAsset = async (assetId) => {
    const reason = window.prompt(`Unlock ${assetId}? Enter the reason (required, audit-logged):`);
    if (!reason || reason.trim().length < 3) {
      if (reason !== null) toast.error("A reason of at least 3 characters is required to unlock.");
      return;
    }
    try {
      await api.post(`/admin/assets/${assetId}/unprogram`, { reason: reason.trim() }, authed());
      toast.success(`${assetId} unlocked (audit-logged)`);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const copyUrl = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopiedUrl(url);
      setTimeout(() => setCopiedUrl(null), 1500);
    } catch {
      toast.error("Could not copy — select and copy manually.");
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-ink flex items-center justify-center px-6" data-testid="admin-login">
        <form onSubmit={login} className="w-full max-w-sm bg-white rounded-sm p-8">
          <p className="font-display text-2xl font-black text-ink mb-1">Revora Admin</p>
          <p className="text-xs text-black/45 mb-8">Sign in to manage orders and assets.</p>
          <input data-testid="admin-email" className={`${INPUT} mb-3`} placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} />
          <input data-testid="admin-password" type="password" className={`${INPUT} mb-4`} placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} />
          {loginError && <p data-testid="admin-login-error" className="text-xs text-red-500 mb-4">{loginError}</p>}
          <button data-testid="admin-login-submit" className="w-full bg-ink text-white font-bold py-3.5 rounded-full hover:bg-volt transition-colors duration-300">
            Sign in
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-mist" data-testid="admin-dashboard">
      <header className="bg-ink text-white">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/" className="inline-flex items-center gap-2 text-sm font-bold text-white/60 hover:text-white transition-colors">
              <ArrowLeft className="w-4 h-4" /> Site
            </Link>
            <span className="font-display font-black">Revora Admin</span>
          </div>
          <button
            data-testid="admin-logout"
            onClick={() => { localStorage.removeItem("revora_admin"); setToken(""); }}
            className="inline-flex items-center gap-2 text-sm text-white/60 hover:text-white transition-colors"
          >
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-12 space-y-14">
        {analytics && (
          <section data-testid="admin-analytics">
            <h2 className="font-display text-2xl font-black text-ink mb-6 flex items-center gap-2"><Activity className="w-5 h-5 text-volt" /> Interactions</h2>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
              {[
                { label: "Total interactions", value: analytics.total_interactions },
                { label: "NFC taps", value: analytics.by_source.nfc },
                { label: "Untagged", value: analytics.by_source.unknown },
                { label: "Total assets", value: analytics.total_assets },
                { label: "Active assets", value: analytics.active_assets },
              ].map((s) => (
                <div key={s.label} className="bg-white border border-black/10 rounded-sm p-5" data-testid={`stat-${s.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                  <p className="font-display text-3xl font-black text-ink">{s.value}</p>
                  <p className="text-[10px] font-bold tracking-[0.15em] uppercase text-black/40 mt-1">{s.label}</p>
                </div>
              ))}
            </div>
            {analytics.top_assets.length > 0 && (
              <div className="mt-4 bg-white border border-black/10 rounded-sm p-5">
                <p className="text-xs font-bold tracking-[0.15em] uppercase text-black/40 mb-3">Most active assets</p>
                <div className="flex flex-wrap gap-2">
                  {analytics.top_assets.map((a) => (
                    <span key={a.asset_id} className="text-xs bg-mist border border-black/10 rounded-full px-3 py-1.5">
                      <strong>{a.asset_id}</strong> · {a.interaction_count} interactions{a.business_name ? ` · ${a.business_name}` : ""}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </section>
        )}

        <section data-testid="admin-orders">
          <h2 className="font-display text-2xl font-black text-ink mb-6">Orders ({orders.length})</h2>
          <div className="bg-white border border-black/10 rounded-sm overflow-x-auto">
            <table className="w-full text-sm min-w-[1000px]">
              <thead>
                <tr className="text-left text-xs font-bold tracking-[0.15em] uppercase text-black/40 border-b border-black/10">
                  <th className="px-5 py-4">Order</th>
                  <th className="px-5 py-4">Business</th>
                  <th className="px-5 py-4">Review URL</th>
                  <th className="px-5 py-4">Package</th>
                  <th className="px-5 py-4">Contact</th>
                  <th className="px-5 py-4">Status</th>
                  <th className="px-5 py-4">Assets</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((o) => (
                  <>
                    <tr key={o.order_number} className="border-b border-black/5" data-testid={`admin-order-${o.order_number}`}>
                      <td className="px-5 py-4 font-bold text-ink whitespace-nowrap">{o.order_number}</td>
                      <td className="px-5 py-4">{o.business_name}</td>
                      <td className="px-5 py-4 max-w-[220px]">
                        <a href={o.google_review_url} target="_blank" rel="noreferrer" className="text-voltdark hover:underline break-all text-xs">
                          {o.google_review_url}
                        </a>
                        {o.url_warning && <p className="text-[10px] text-amber-600 mt-1">{o.url_warning}</p>}
                      </td>
                      <td className="px-5 py-4 whitespace-nowrap">{o.quantity} × ${o.unit_price.toFixed(2)}
                        <p className={`text-[10px] font-bold uppercase tracking-[0.1em] mt-0.5 ${o.payment === "paid" ? "text-green-600" : "text-amber-600"}`}>{o.payment === "paid" ? "Paid" : "Awaiting payment"}</p>
                      </td>
                      <td className="px-5 py-4 text-xs">{o.contact_name}<br /><span className="text-black/45">{o.email}</span></td>
                      <td className="px-5 py-4">
                        <select
                          data-testid={`admin-status-${o.order_number}`}
                          value={o.status}
                          onChange={(e) => setStatus(o.order_number, e.target.value)}
                          className="border border-black/15 rounded-sm text-xs px-2 py-1.5 bg-white focus:outline-none focus:ring-1 focus:ring-volt"
                        >
                          {statuses.map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                      </td>
                      <td className="px-5 py-4 whitespace-nowrap">
                        {o.assigned_assets?.length ? (
                          <button
                            data-testid={`view-items-${o.order_number}`}
                            onClick={() => setExpandedOrder(expandedOrder === o.order_number ? null : o.order_number)}
                            className="text-xs font-bold text-voltdark inline-flex items-center gap-1"
                          >
                            {o.assigned_assets.length} item{o.assigned_assets.length > 1 ? "s" : ""} <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${expandedOrder === o.order_number ? "rotate-180" : ""}`} />
                          </button>
                        ) : o.payment === "paid" ? (
                          <button
                            data-testid={`assign-assets-${o.order_number}`}
                            onClick={() => assignAssets(o.order_number)}
                            className="text-xs font-bold bg-ink text-white px-3.5 py-1.5 rounded-full hover:bg-volt transition-colors duration-200"
                          >
                            Assign assets
                          </button>
                        ) : (
                          <span className="text-xs text-black/35">—</span>
                        )}
                      </td>
                    </tr>
                    {expandedOrder === o.order_number && (
                      <tr key={`${o.order_number}-items`} className="border-b border-black/5 bg-mist/60">
                        <td colSpan={7} className="px-5 py-5">
                          <p className="text-[10px] font-bold tracking-[0.15em] uppercase text-black/45 mb-3">
                            NFC items for {o.order_number} — copy each URL onto its NFC item before shipping
                          </p>
                          <div className="space-y-2" data-testid={`order-items-${o.order_number}`}>
                            {(o.asset_items || []).map((a, i) => (
                              <div key={a.asset_id} className="bg-white border border-black/10 rounded-sm px-4 py-3 flex flex-wrap items-center gap-3">
                                <span className="text-[10px] font-bold text-black/30 w-6">#{i + 1}</span>
                                <span className="font-bold text-ink text-sm" data-testid={`order-item-id-${a.asset_id}`}>{a.asset_id}</span>
                                <code className="text-xs font-mono text-black/60 bg-mist px-2 py-1 rounded break-all" data-testid={`order-item-url-${a.asset_id}`}>{a.permanent_url}</code>
                                <button
                                  data-testid={`copy-url-${a.asset_id}`}
                                  onClick={() => copyUrl(a.permanent_url)}
                                  className="inline-flex items-center gap-1 text-xs font-bold text-voltdark hover:text-ink transition-colors"
                                >
                                  {copiedUrl === a.permanent_url ? <Check className="w-3.5 h-3.5 text-green-600" /> : <Copy className="w-3.5 h-3.5" />}
                                  {copiedUrl === a.permanent_url ? "Copied" : "Copy"}
                                </button>
                                <span className={`text-[10px] font-bold tracking-[0.1em] uppercase px-2.5 py-1 rounded-full ${a.destination_url ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}`} data-testid={`order-item-dest-${a.asset_id}`}>
                                  {a.destination_url ? "Destination set" : "No destination"}
                                </span>
                                <span className={`text-[10px] font-bold tracking-[0.1em] uppercase px-2.5 py-1 rounded-full ${a.programmed ? "bg-ink text-white" : "bg-black/10 text-black/50"}`} data-testid={`order-item-prog-${a.asset_id}`}>
                                  {a.programmed ? "Programmed" : "Not programmed"}
                                </span>
                                {a.first_interaction_at && (
                                  <span className="text-[10px] text-black/40" data-testid={`order-item-firsttap-${a.asset_id}`}>
                                    first tap {new Date(a.first_interaction_at).toLocaleString()} (hint only)
                                  </span>
                                )}
                                <span className="ml-auto flex gap-2">
                                  {!a.programmed ? (
                                    <button data-testid={`program-${a.asset_id}`} onClick={() => programAsset(a.asset_id)} className="inline-flex items-center gap-1 text-xs font-bold bg-volt text-ink px-3 py-1.5 rounded-full hover:bg-voltdark hover:text-white transition-colors">
                                      <Lock className="w-3 h-3" /> Mark programmed
                                    </button>
                                  ) : (
                                    <button data-testid={`unprogram-${a.asset_id}`} onClick={() => unprogramAsset(a.asset_id)} className="inline-flex items-center gap-1 text-xs font-bold text-black/50 hover:text-ink transition-colors">
                                      <Unlock className="w-3 h-3" /> Unlock…
                                    </button>
                                  )}
                                </span>
                              </div>
                            ))}
                            {(!o.asset_items || o.asset_items.length === 0) && (
                              <p className="text-xs text-black/45">No items created yet.</p>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </>
                ))}
                {orders.length === 0 && (
                  <tr><td colSpan={7} className="px-5 py-10 text-center text-black/40">No orders yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </section>

        <section data-testid="admin-assets">
          <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
            <h2 className="font-display text-2xl font-black text-ink flex items-center gap-2"><Link2 className="w-5 h-5 text-volt" /> Card Assets ({assets.length})</h2>
            <div className="flex gap-2">
              <button data-testid="generate-assets" onClick={() => generate(false)} className="inline-flex items-center gap-2 text-xs font-bold bg-ink text-white px-4 py-2.5 rounded-full hover:bg-volt transition-colors duration-200">
                <Plus className="w-3.5 h-3.5" /> Generate 10 assets
              </button>
              <button data-testid="generate-test-assets" onClick={() => generate(true)} className="inline-flex items-center gap-2 text-xs font-bold border border-black/15 px-4 py-2.5 rounded-full hover:border-ink transition-colors duration-200">
                <Plus className="w-3.5 h-3.5" /> Generate test assets
              </button>
            </div>
          </div>
          <div className="bg-white border border-black/10 rounded-sm overflow-x-auto">
            <table className="w-full text-sm min-w-[900px]">
              <thead>
                <tr className="text-left text-xs font-bold tracking-[0.15em] uppercase text-black/40 border-b border-black/10">
                  <th className="px-5 py-4">Asset</th>
                  <th className="px-5 py-4">Status</th>
                  <th className="px-5 py-4">Business</th>
                  <th className="px-5 py-4">Destination</th>
                  <th className="px-5 py-4">Interactions</th>
                  <th className="px-5 py-4"></th>
                </tr>
              </thead>
              <tbody>
                {assets.map((a) => (
                  <>
                    <tr key={a.asset_id} className="border-b border-black/5" data-testid={`asset-row-${a.asset_id}`}>
                      <td className="px-5 py-3.5">
                        <p className="font-bold text-ink">{a.asset_id}</p>
                        <p className="text-[10px] text-black/40 font-mono">{a.permanent_url}</p>
                      </td>
                      <td className="px-5 py-3.5">
                        <span className={`text-[10px] font-bold tracking-[0.1em] uppercase px-2.5 py-1 rounded-full ${STATUS_COLORS[a.status] || ""}`}>{a.status}</span>
                      </td>
                      <td className="px-5 py-3.5 text-xs">
                        {a.business_name || <span className="text-black/30">—</span>}
                        {(a.location_name || a.staff_name) && (
                          <p className="text-[10px] text-black/40">{[a.location_name, a.staff_name].filter(Boolean).join(" · ")}</p>
                        )}
                      </td>
                      <td className="px-5 py-3.5 max-w-[200px]">
                        {a.destination_url ? (
                          <a href={a.destination_url} target="_blank" rel="noreferrer" className="text-voltdark hover:underline break-all text-xs">{a.destination_url}</a>
                        ) : (
                          <span className="text-black/30 text-xs">Not configured</span>
                        )}
                      </td>
                      <td className="px-5 py-3.5 text-xs font-bold">{a.interaction_count}</td>
                      <td className="px-5 py-3.5">
                        <button data-testid={`edit-asset-${a.asset_id}`} onClick={() => openEdit(a)} className="text-xs font-bold text-volt inline-flex items-center gap-1">
                          Manage <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${expanded === a.asset_id ? "rotate-180" : ""}`} />
                        </button>
                      </td>
                    </tr>
                    {expanded === a.asset_id && (
                      <tr key={`${a.asset_id}-edit`} className="border-b border-black/5 bg-mist/60">
                        <td colSpan={6} className="px-5 py-5">
                          {a.programming_locked && (
                            <div className="mb-4 bg-amber-50 border border-amber-200 rounded-sm px-4 py-3 text-xs text-amber-700" data-testid={`locked-note-${a.asset_id}`}>
                              This item is marked as programmed onto a physical NFC product. Its asset ID, Revora redirect URL
                              and order assignment are locked. You can still correct the redirect destination below — the destination
                              will change, but the URL on the NFC item stays the same. A reason is required and every change is audit-logged.
                            </div>
                          )}
                          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3 items-end">
                            <div className="lg:col-span-2">
                              <label className="block text-[10px] font-bold tracking-[0.15em] uppercase text-black/45 mb-1.5">Destination URL</label>
                              <input data-testid={`asset-destination-${a.asset_id}`} className={INPUT} value={edit.destination_url} onChange={(e) => setEdit({ ...edit, destination_url: e.target.value })} placeholder="https://g.page/r/…/review" />
                            </div>
                            <div>
                              <label className="block text-[10px] font-bold tracking-[0.15em] uppercase text-black/45 mb-1.5">Business</label>
                              <input className={INPUT} value={edit.business_name} onChange={(e) => setEdit({ ...edit, business_name: e.target.value })} />
                            </div>
                            <div>
                              <label className="block text-[10px] font-bold tracking-[0.15em] uppercase text-black/45 mb-1.5">Location</label>
                              <input className={INPUT} value={edit.location_name} onChange={(e) => setEdit({ ...edit, location_name: e.target.value })} />
                            </div>
                            <div>
                              <label className="block text-[10px] font-bold tracking-[0.15em] uppercase text-black/45 mb-1.5">Staff</label>
                              <input className={INPUT} value={edit.staff_name} onChange={(e) => setEdit({ ...edit, staff_name: e.target.value })} />
                            </div>
                            <div>
                              <label className="block text-[10px] font-bold tracking-[0.15em] uppercase text-black/45 mb-1.5">Status</label>
                              <select className={INPUT} value={edit.status} onChange={(e) => setEdit({ ...edit, status: e.target.value })}>
                                {assetStatuses.map((s) => <option key={s} value={s}>{s}</option>)}
                              </select>
                            </div>
                            {a.programming_locked && (
                              <div className="lg:col-span-5">
                                <label className="block text-[10px] font-bold tracking-[0.15em] uppercase text-amber-700 mb-1.5">Reason for change (required, audit-logged)</label>
                                <input data-testid={`asset-reason-${a.asset_id}`} className={INPUT} value={edit.reason} onChange={(e) => setEdit({ ...edit, reason: e.target.value })} placeholder="e.g. Customer provided corrected review URL" />
                              </div>
                            )}
                            <div className="flex gap-2">
                              <button data-testid={`save-asset-${a.asset_id}`} onClick={() => saveAsset(a.asset_id, a)} className="inline-flex items-center gap-2 bg-volt text-ink text-xs font-bold px-5 py-3 rounded-full hover:bg-voltdark hover:text-white transition-colors duration-200">
                                <Save className="w-3.5 h-3.5" /> Save
                              </button>
                              <a data-testid={`test-asset-${a.asset_id}`} href={`/r/${a.asset_id}`} target="_blank" rel="noreferrer" className="inline-flex items-center border border-black/15 text-xs font-bold px-5 py-3 rounded-full hover:border-ink transition-colors duration-200">
                                Test route
                              </a>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </>
                ))}
                {assets.length === 0 && (
                  <tr><td colSpan={6} className="px-5 py-10 text-center text-black/40">No assets yet. Generate a batch to get started.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </section>

        <section data-testid="admin-packages">
          <h2 className="font-display text-2xl font-black text-ink mb-6">Package pricing</h2>
          <div className="bg-white border border-black/10 rounded-sm p-6 grid grid-cols-2 sm:grid-cols-5 gap-4 items-end">
            {packages.map((p, i) => (
              <div key={p.id}>
                <label className="block text-xs font-bold tracking-[0.15em] uppercase text-black/45 mb-2">{p.quantity} cards</label>
                <input
                  data-testid={`admin-price-${p.quantity}`}
                  type="number"
                  step="0.01"
                  className={INPUT}
                  value={p.price}
                  onChange={(e) => {
                    const v = parseFloat(e.target.value) || 0;
                    setPackages((ps) => ps.map((x, j) => (j === i ? { ...x, price: v } : x)));
                  }}
                />
              </div>
            ))}
            <button data-testid="admin-save-prices" onClick={savePrices} className="inline-flex items-center justify-center gap-2 bg-ink text-white font-bold px-6 py-3 rounded-full hover:bg-volt transition-colors duration-300">
              <Save className="w-4 h-4" /> Save
            </button>
          </div>
        </section>
      </main>
    </div>
  );
}
